import * as React from 'react';
import {observer} from 'mobx-react';
import {centerCenter, flex1, horizontal, horizontallySpaced} from 'csstips';
import {classes, media, style} from 'typestyle';
import {important, percent, px} from 'csx';
import {colorContrast, colorSlate, colorWhite} from '../theme/color';
import {mediaMobileOnly, mediaTablet} from '../theme/media';
import {PlayerBarControls} from './player-bar-controls';
import {PlayerBarNowPlaying} from './player-bar-now-playing';
import {PlayerBarVolume} from './player-bar-volume';
import {gradientTrackTile} from '../theme/gradient';
import {formatDuration} from './duration';
import {AudioMetaSchema, TrackSchema} from '../types/schema';
import {ImagePreloader} from '../types/fn';
import Player, {PlayerAttachPlayListener} from './player/player';
import {User} from '../types/user';
import {resetAnchorStyles} from '../theme/reset';

export interface PlayerBarModel {
  track: TrackSchema
  duration: number;
  isPlaying: boolean;
  startAt: number | null;
  volume: number;
  user: User;
  signInHref: string;
}

export interface PlayerBarProps {
  model: PlayerBarModel;
  AddRemove: any;
  Favourite: any;
  es6?: boolean;
  className?: string;
  preload: ImagePreloader;
  preloadWaveform: ImagePreloader;
  loadAudioMeta(trackId: string): Promise<AudioMetaSchema>;
  onClickSignIn(event: React.MouseEvent<any>): void;
  onClickPlay(event: React.MouseEvent<any>): void;
  onClickPause(event: React.MouseEvent<any>): void;
  onClickNext(event: React.MouseEvent<any>): void;
  onClickPrevious(event: React.MouseEvent<any>): void;
  onChangeVolume(volume: number): void;
  onFinish(): void;
  onMeta?(meta: AudioMetaSchema): any;
  onClickLink(event: React.MouseEvent<any>): void;
  attachPlayListener: PlayerAttachPlayListener;
  i18n?: {
    shunt: string;
    signIn: string;
  }
}

export interface PlayerBarState {
  pulse: boolean;
}

const constants = {
  height: px(70),
  shunt: px(24),
};

@observer
export class PlayerBar extends React.Component<PlayerBarProps, PlayerBarState> {
  _mounted = false;

  constructor(props) {
    super(props);
    this.state = {pulse: false};
  }

  render() {
    const { props } = this;
    const { model, AddRemove, Favourite, preload, preloadWaveform, onClickLink, i18n = PlayerBar.i18n } = props;

    const className = classes(
      props.className,
      PlayerBar.styles.container,
      !model.user && PlayerBar.styles.containerShunted
    );

    return (
      <div className={className}>
        {!model.user && (
          <div className={classes(PlayerBar.styles.shunt, this.state.pulse && PlayerBar.styles.shuntPulse)}>
            <span>{i18n.shunt}</span>
            <a href={model.signInHref} className={PlayerBar.styles.signIn} onClick={props.onClickSignIn}>{i18n.signIn}</a>
          </div>
        )}
        <div className={classes(PlayerBar.styles.innerContainer, PlayerBar.styles.innerContainerMain)}>
          <PlayerBarControls
            model={model}
            onClickPlay={props.onClickPlay}
            onClickPause={props.onClickPause}
            onClickNext={props.onClickNext}
            onClickPrevious={props.onClickPrevious}
            preload={preload}
          />
          <PlayerBarNowPlaying
            model={model}
            onClickLink={onClickLink}
          />
          <Player
            user={model.user}
            className={PlayerBar.styles.waveform}
            play={model.isPlaying}
            startAt={model.startAt}
            trackId={model.track ? model.track.identity : void 0}
            loadAudioMeta={props.loadAudioMeta}
            preload={preload}
            preloadWaveform={preloadWaveform}
            onFinish={this.handleFinish}
            onMeta={props.onMeta}
            volume={model.volume}
            attachPlayListener={props.attachPlayListener}
          />
          {model.duration && (
            <div className={PlayerBar.styles.duration}>
              <span>{formatDuration(model.duration)}</span>
            </div>
          )}
          <PlayerBarVolume
            volume={model.volume}
            onChangeVolume={props.onChangeVolume}
          />
          <div className={PlayerBar.styles.addRemove}>
            <Favourite track={model.track} />
            <span className={PlayerBar.styles.hideMobile}>
              <AddRemove track={model.track} withLicense={true} reverse={true}/>
            </span>
            <span className={PlayerBar.styles.hideDesktop}>
              <AddRemove track={model.track}/>
            </span>
          </div>
        </div>
        <div className={classes(PlayerBar.styles.innerContainer, PlayerBar.styles.innerContainerFallback)} />
      </div>
    )
  }

  componentDidMount() {
    this._mounted = true;
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  handleFinish = () => {
    if (this.props.onFinish) {
      this.props.onFinish();
    }

    this.setState({pulse: true}, () => {
      setTimeout(() => {
        if (this._mounted) this.setState({pulse: false})
      }, 420)
    })
  };

  static i18n = {
    shunt: 'Preview the full track when you ',
    signIn: 'register / sign in'
  };

  static styles = {
    container: style({
      height: constants.height,
      background: colorSlate.darken(0.05).toString()
    }),
    containerShunted: style({
      marginBottom: constants.shunt
    }),
    innerContainer: style({
      ...horizontal,
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    }),
    innerContainerMain: style({
      zIndex: 2
    }),
    innerContainerFallback: style(
      {
        zIndex: 1,
      },
      mediaMobileOnly({
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundImage: gradientTrackTile
      })
    ),
    waveform: style(
      {
        ...flex1,
        height: px(50),
        margin: '10px 0',
        zIndex: 3
      },
      mediaMobileOnly({
        position: important('absolute'),
        display: 'block',
        bottom: constants.height,
        left: 0,
        right: 0,
        margin: 0,
        height: px(6),
        $nest: {
          '&> div': {
            borderRadius: important(0),
          },
          '&> div > wave': {
            borderRadius: important(0),
          },
        }
      })
    ),
    addRemove: style({
      ...centerCenter,
      ...horizontal,
      ...horizontallySpaced(5),
      height: percent(100),
      padding: '0 16px 0 0',
    }),
    duration: style(
      {
        ...centerCenter,
        padding: '0 0 0 16px',
        fontSize: px(15),
        height: percent(100),
        lineHeight: px(14),
        color: colorWhite.toString()
      },
      media({ maxWidth: 390 }, {
        display: 'none'
      })
    ),
    hideMobile: style(mediaMobileOnly({ display: 'none' })),
    hideDesktop: style(mediaTablet({ display: 'none' })),
    shunt: style({
      position: 'absolute',
      top: percent(100),
      left: 0,
      right: 0,
      height: constants.shunt,
      background: colorSlate.darken(0.075).toString(),
      padding: '0 16px',
      textAlign: 'center',
      fontSize: px(12),
      lineHeight: constants.shunt,
      whiteSpace: 'pre',
      fontWeight: 500,
      color: colorWhite.toString(),
      $nest: {
        '> *': {
          opacity: 1,
          transition: 'opacity 0.25s ease-out'
        }
      }
    }),
    shuntPulse: style({
      $nest: {
        '> *': {
          opacity: 0,
          transition: 'opacity 0.25s ease-in'
        }
      }
    }),
    signIn: style({
      color: colorContrast.toString(),
      $nest: {
        ...resetAnchorStyles(colorContrast.toString())
      }
    })
  }
}